import { styled } from '@linaria/react';
import { Group, Select } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { MeasureMetadata, WorkPeriod } from '../../../api/work-periods-client/work-periods-client.type.ts';
import { useTranslation } from 'react-i18next';
import { canvasHtmlDownload } from '../../../helpers/image-downloader/image-downloader.ts';
import { ChartType } from '../../../store/financials-store/financials-store.type.ts';
import { useGlobalStore } from '../../../store/global-store/global-store.ts';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store.ts';
import { baseWhite, secondaryBase } from '../../../styles/design-tokens.ts';
import { Button } from '../../../ui-library/button/button.tsx';
import { Icon } from '../../../ui-library/icon/icon.tsx';
import { Text } from '../../../ui-library/typography/typography.tsx';
import { FloraButton } from '../../flora/flora-button.tsx';
import { WorkPeriodChartHeader } from '../components/work-period-chart-header.tsx';
import { MeasureComparisonChart } from './components/measure-comparison-chart.tsx';
import { formatWorkPeriodDataForFlora } from './helpers/measure-comparison-chart.helpers.ts';

interface ComparisonMeasureComparisonProps {
  customMeasures: MeasureMetadata[];
  measureData: Record<string, any>;
  workPeriods: WorkPeriod[];
  handleMeasureClick: (measure: string, workPeriodName: string, title: string, date?: string | null) => void;
}

export const ComparisonMeasureComparison = ({
  customMeasures,
  measureData,
  workPeriods,
  handleMeasureClick,
}: ComparisonMeasureComparisonProps) => {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);
  const downloadRef = useRef<HTMLDivElement>(null!);
  const [chartType, setChartType] = useState<ChartType>(ChartType.Line);
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedMeasuresForComparison, setSelectedMeasuresForComparison } = useWorkPeriodStore();
  const [visibleMeasures, setVisibleMeasures] = useState<Record<string, boolean>>({});
  const didInitMeasuresFromUrlRef = useRef(false);

  // One-time URL parameter initialization for selectedMeasures (from Flora quick links)
  useEffect(() => {
    if (didInitMeasuresFromUrlRef.current) return;

    const urlMeasures = searchParams.get('selectedMeasures');
    if (!urlMeasures || customMeasures.length === 0) return;

    const measureNames = urlMeasures.split(',');
    const matchedMeasures = measureNames
      .map((name) => customMeasures.find((m) => m.measure_name === name))
      .filter((m): m is MeasureMetadata => m != null);

    if (matchedMeasures.length > 0) {
      setSelectedMeasuresForComparison(matchedMeasures);
    }

    didInitMeasuresFromUrlRef.current = true;

    const newParams = new URLSearchParams(searchParams);
    newParams.delete('selectedMeasures');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, customMeasures, setSelectedMeasuresForComparison]);

  useEffect(() => {
    const initialVisibility: Record<string, boolean> = {};
    selectedMeasuresForComparison.forEach((measure) => {
      initialVisibility[measure.measure_name] = true;
    });
    setVisibleMeasures(initialVisibility);
  }, [selectedMeasuresForComparison]);

  const handleMeasureToggle = (measureName: string) => {
    setVisibleMeasures((prev) => ({
      ...prev,
      [measureName]: !prev[measureName],
    }));
  };

  const handleAddMeasure = (measureTitle: string | null) => {
    if (!measureTitle) return;

    const measureToAdd = customMeasures.find((m) => m.measure_title === measureTitle);
    if (measureToAdd && !selectedMeasuresForComparison.some((m) => m.measure_name === measureToAdd.measure_name)) {
      setSelectedMeasuresForComparison([...selectedMeasuresForComparison, measureToAdd]);
    }
  };

  const handleRemoveMeasure = (measureName: string) => {
    setSelectedMeasuresForComparison(selectedMeasuresForComparison.filter((m) => m.measure_name !== measureName));
  };

  const availableMeasures = customMeasures
    .filter(
      (measure) => !selectedMeasuresForComparison.some((selected) => selected.measure_name === measure.measure_name),
    )
    .map((measure) => ({ value: measure.measure_title, label: measure.measure_title }));

  const handleDownload = () => {
    if (downloadRef.current) {
      try {
        canvasHtmlDownload(`${i18n.t('common.sprint')}_comparison_measure_comparison`, downloadRef);
      } catch (error) {
        console.error('Error downloading chart', error);
      }
    }
  };
  const floraData = formatWorkPeriodDataForFlora(
    measureData,
    customMeasures,
    workPeriods,
    selectedMeasuresForComparison.map((measure) => measure.measure_name),
  );

  const chartTypeControls = (
    <Group>
      {[ChartType.Line, ChartType.Bar].map((buttonChartType) => {
        const isSelected = buttonChartType === chartType;
        return (
          <Button
            key={buttonChartType}
            radius="xl"
            size="xs"
            variant={isSelected ? 'secondary' : 'outline'}
            onClick={() => setChartType(buttonChartType)}
            leftSection={
              <Icon
                name={buttonChartType === ChartType.Line ? 'stacked_line_chart' : 'bar_chart'}
                color={isSelected ? baseWhite : secondaryBase}
                size={16}
              />
            }
          >
            {buttonChartType === ChartType.Line ? 'Line Chart' : 'Bar Chart'}
          </Button>
        );
      })}
    </Group>
  );

  return (
    <div ref={downloadRef} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <WorkPeriodChartHeader
        title=" Measure Comparison"
        onExport={handleDownload}
        hideRadio
        additionalControls={chartTypeControls}
        floraButton={
          useAI && (
            <FloraButton
              externalData={[floraData]}
              prompt={i18n.t('flora.work_periods.sprint_measure_comparison_chart_prompt')}
              tooltipText={i18n.t('flora.work_periods.flora_button_tooltip')}
            />
          )
        }
      />
      <Group mt="16px" mb="16px" justify="space-between">
        <Group wrap="wrap">
          {selectedMeasuresForComparison.length > 0 ? (
            selectedMeasuresForComparison.map((measure) => (
              <MeasureTag
                key={measure.measure_name}
                title={measure.measure_title}
                onRemove={() => handleRemoveMeasure(measure.measure_name)}
              />
            ))
          ) : (
            <Text color="dimmed">No measures selected</Text>
          )}
        </Group>
        <Group>
          <Text weight="medium">Add measures</Text>
          {customMeasures.length > 0 && (
            <Select
              data={availableMeasures}
              value={null}
              onChange={handleAddMeasure}
              placeholder={availableMeasures.length === 0 ? 'All measures added' : 'Select a measure'}
              disabled={availableMeasures.length === 0}
              radius="xl"
            />
          )}
        </Group>
      </Group>
      <div style={{ flex: 1, minHeight: 0 }}>
        <MeasureComparisonChart
          customMeasures={customMeasures}
          measureData={measureData}
          workPeriods={workPeriods}
          chartType={chartType}
          selectedMeasures={selectedMeasuresForComparison}
          visibleMeasures={visibleMeasures}
          onMeasureToggle={handleMeasureToggle}
          onMeasureClick={handleMeasureClick}
        />
      </div>
    </div>
  );
};

interface MeasureTagProps {
  title: string;
  onRemove: () => void;
}

const MeasureTag = ({ title, onRemove }: MeasureTagProps) => {
  return (
    <StyledMeasureTag>
      <Text size="small" weight="medium">
        {title}
      </Text>
      <RemoveButton onClick={onRemove} title="Remove measure">
        <Icon name="close" size={16} style={{ cursor: 'pointer' }} />
      </RemoveButton>
    </StyledMeasureTag>
  );
};

const StyledMeasureTag = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${baseWhite};
  border: 1px solid ${secondaryBase};
  border-radius: 16px;
  padding: 4px 8px 4px 12px;
  font-size: 12px;
  color: ${secondaryBase};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background-color: transparent;
  color: ${secondaryBase};
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${secondaryBase}20;
  }

  &:active {
    background-color: ${secondaryBase}30;
  }
`;
